/**
 * `createApiOrgUsersSource` — the permissions-mgmt-api org-user port over the
 * shared `ApiClient`.
 *
 * Backs the Team page's "Organization members" list + the "Invite people"
 * dialog: listing the org's members, the org role catalog for the role picker,
 * and inviting a person into the organization. The wire is snake_case
 * (`user_id`, `tenant_id`, `role_name`); this adapter maps it to the camelCase
 * summaries the UI renders. Canonical paths (`/org-users[/invite]`,
 * `/org-roles`) are rewritten onto the `.management.` permissions zone by the
 * dashboard-local rules in `app/api-client.ts`, so region routing / auth /
 * envelope unwrap are inherited.
 *
 * RBAC: list → `users:list`, roles → `roles:list`, invite → `users:create`
 * (the customer-facing scopes; internal staff management uses `internal-*`).
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

export interface OrgMember {
  id: string;
  email: string;
  name?: string;
  /** Org role names the member holds (permissions returns names, not ids). */
  roles: string[];
  /** `active` | `pending_invitation` | `suspended` | `inactive`. */
  status: string;
  /**
   * The invitation is still outstanding — the person was invited but has never
   * logged in (derived server-side from Auth0 login state). Drives the "Pending"
   * badge and the "Cancel invitation" (vs. "Remove") row action.
   */
  pending: boolean;
}

export interface OrgRole {
  /** Token to send in an invite: a default role's id IS its name; custom = `rol_…`. */
  id: string;
  name: string;
  description?: string;
}

export interface InviteOrgUserInput {
  email: string;
  /** Org role tokens (default-role names or custom role ids). */
  roles: string[];
  /** Root/home tenant for the invitee. */
  tenantId: string;
}

export interface OrgUsersSource {
  /** Members of the caller's organization (permissions `users:list`). */
  listMembers(): Promise<OrgMember[]>;
  /** Org role catalog for the invite role picker (permissions `roles:list`). */
  listRoles(): Promise<OrgRole[]>;
  /** Invite a person into the organization (permissions `users:create`). */
  invite(input: InviteOrgUserInput): Promise<void>;
  /**
   * Remove a member from the caller's org, or cancel their pending invitation
   * (permissions `users:delete`). The backend decides which: a never-logged-in
   * invitee is hard-canceled (Auth0 account + invite link invalidated), an
   * accepted member is soft-removed. Throws a typed `ApiError` on failure
   * (e.g. 404 not a member, 400 when you try to remove yourself).
   */
  remove(userId: string): Promise<void>;
}

/** Coerce the permissions success envelope into a plain array of rows. */
function rowsFromWire(data: unknown): Record<string, any>[] {
  if (Array.isArray(data)) return data as Record<string, any>[];
  const items = (data as { items?: unknown; result?: unknown } | null)?.items;
  return Array.isArray(items) ? (items as Record<string, any>[]) : [];
}

function memberFromWire(raw: Record<string, any>): OrgMember {
  const roles = Array.isArray(raw.roles) ? raw.roles.map((r: unknown) => String(r)) : [];
  return {
    id: String(raw.user_id ?? raw.id ?? ""),
    email: String(raw.email ?? ""),
    name: raw.name ?? undefined,
    roles,
    status: String(raw.status ?? ""),
    // permissions derives `pending` from Auth0 login state; legacy rows may only
    // carry a `pending_invitation` status, so treat that as pending too.
    pending: raw.pending === true || String(raw.status ?? "") === "pending_invitation",
  };
}

function roleFromWire(raw: Record<string, any>): OrgRole {
  const id = String(raw.id ?? raw.role_name ?? raw.name ?? "");
  return {
    id,
    name: String(raw.role_name ?? raw.name ?? id),
    description: raw.description ?? undefined,
  };
}

export function createApiOrgUsersSource(client: ApiClient): OrgUsersSource {
  return {
    async listMembers(): Promise<OrgMember[]> {
      const res = await client.get<Record<string, any>>("/org-users");
      return rowsFromWire(unwrapData(res))
        .map(memberFromWire)
        .filter((m) => m.email || m.id);
    },

    async listRoles(): Promise<OrgRole[]> {
      const res = await client.get<Record<string, any>>("/org-roles");
      return rowsFromWire(unwrapData(res))
        .map(roleFromWire)
        .filter((r) => r.id);
    },

    async invite(input: InviteOrgUserInput): Promise<void> {
      // unwrapData throws a typed ApiError on an `ok:false` envelope (e.g. a
      // 409 when the person is already an active member) so the dialog can react.
      unwrapData(
        await client.post<Record<string, any>>("/org-users/invite", {
          email: input.email,
          roles: input.roles,
          tenant_id: input.tenantId,
        }),
      );
      client.invalidate("/org-users");
    },

    async remove(userId: string): Promise<void> {
      // DELETE /org-users/{id} → permissions `DELETE /v0/users/{id}` (org taken
      // from the auth token). unwrapData throws a typed ApiError on `ok:false`
      // (e.g. 404 not a member, 400 self-removal) so the caller can react.
      unwrapData(
        await client.delete<Record<string, any>>(`/org-users/${encodeURIComponent(userId)}`),
      );
      client.invalidate("/org-users");
    },
  };
}
